import React, { type FC, type JSX } from 'react';
import {
  FormLabel,
  Grid,
  GridItem,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Switch,
  Tooltip,
  Text,
} from '@chakra-ui/react';
import cx from 'classnames';
import ChartLayerSelector from './ChartLayerSelector';
import { ReactComponent as ChartsIcon } from '../../../../assets/svg/Charts.svg';
import { MapLayer } from '../../types';
import { updateSettings } from '../../utils/chart-settings-utils';
import { useDispatch, useMapContext } from '../../../GlobalContextProvider/GlobalContextProvider';
import { useAppInsights } from '../../../../hooks/useAppInsights';
import { getEventFactory } from '../../../../utils/get-event-factory';
import { Event, type Page } from '../../../../types';
import { translate } from '../../../../constants/translation';

export interface Props {
  page: Page;
  operation: string;
  isTripsPanelOpen: boolean;
}

const ChartSettings: FC<Props> = ({ page, operation, isTripsPanelOpen }): JSX.Element => {
  const {
    settings,
    settings: { showVesselTracks, showVesselNames, showQuiverPlot, showNauticalMap },
  } = useMapContext();
  const dispatch = useDispatch();
  const appInsights = useAppInsights();

  const handleLayerSelection = (layer: MapLayer): void => {
    updateSettings({ ...settings, layer }, dispatch);
  };

  const handleVesselTracksToggle = (): void => {
    const eventType = !showVesselTracks ? Event.VesselTracksEnabled : Event.VesselTracksDisabled;
    const event = getEventFactory(eventType)(page, operation);
    appInsights.trackAnalyticsEvent(event);
    updateSettings({ ...settings, showVesselTracks: !showVesselTracks }, dispatch);
  };

  const handleVesselNamesToggle = (): void => {
    const eventType = showVesselNames ? Event.DisableVesselNames : Event.EnableVesselNames;
    const event = getEventFactory(eventType)(page, operation);
    appInsights.trackAnalyticsEvent(event);

    updateSettings({ ...settings, showVesselNames: !showVesselNames }, dispatch);
  };

  const handleQuiverPlotToggle = (): void => {
    const eventType = showQuiverPlot ? Event.DisableQuiverPlot : Event.EnableQuiverPlot;
    const event = getEventFactory(eventType)(page, operation);
    appInsights.trackAnalyticsEvent(event);

    updateSettings({ ...settings, showQuiverPlot: !showQuiverPlot }, dispatch);
  };

  const handleNauticalToggle = (): void => {
    const eventType = showNauticalMap ? Event.DisableNauticalMap : Event.EnableNauticalMap;
    const event = getEventFactory(eventType)(page, operation);
    appInsights.trackAnalyticsEvent(event);
    updateSettings({ ...settings, showNauticalMap: !showNauticalMap }, dispatch);
  };

  return (
    <Popover closeOnBlur isLazy strategy="fixed">
      <Tooltip placement="bottom-start" label={translate('CHART_SETTINGS')}>
        <PopoverTrigger>
          <IconButton
            variant="secondary"
            className="chart-settings-trigger"
            icon={<ChartsIcon />}
            aria-label={translate('CHART_SETTINGS')}
          />
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent className={cx('chart-settings', { expanded: isTripsPanelOpen })}>
        <PopoverBody>
          <Grid templateColumns="1fr 1fr 1fr 1fr 1fr 1fr">
            <GridItem colSpan={6}>
              <FormLabel className="settings-group-label">{translate('CHART_LAYERS')}</FormLabel>
            </GridItem>
            <GridItem colSpan={1} className="chart-selector-container">
              <ChartLayerSelector chartLayer={MapLayer.Default} onLayerClick={handleLayerSelection} />
            </GridItem>
            <GridItem colSpan={1} className="chart-selector-container">
              <ChartLayerSelector chartLayer={MapLayer.Satellite} onLayerClick={handleLayerSelection} />
            </GridItem>
            <Tooltip
              borderRadius="8px"
              padding="12px"
              placement="left"
              label={
                <Stack>
                  <Text>{translate('NAUTICAL_CHART_HEADING')}</Text>
                  <Text>{translate('NAUTICAL_CHART_TEXT')}</Text>
                </Stack>
              }
            >
              <GridItem colSpan={1} className="chart-selector-container">
                <ChartLayerSelector chartLayer={MapLayer.Nautical} onLayerClick={handleLayerSelection} />
              </GridItem>
            </Tooltip>

            <GridItem />
            <GridItem colSpan={6}>
              <FormLabel className="settings-group-label">{translate('CHART_DETAILS')}</FormLabel>
            </GridItem>
          </Grid>
          <Grid templateColumns="1fr 6fr" templateRows="auto auto" gap="6px">
            <GridItem className="chart-setting-container">
              <Switch onChange={handleVesselTracksToggle} isChecked={showVesselTracks} />
            </GridItem>
            <GridItem>
              <FormLabel className="setting-label">{translate('VESSEL_TRACKS')}</FormLabel>
            </GridItem>
            <GridItem colSpan={2}>
              <FormLabel className="setting-description">{translate('VESSEL_TRACKS_DESC')}</FormLabel>
            </GridItem>
            <GridItem className="chart-setting-container">
              <Switch onChange={handleVesselNamesToggle} isChecked={showVesselNames} />
            </GridItem>
            <GridItem>
              <FormLabel className="setting-label">{translate('VESSEL_NAMES')}</FormLabel>
            </GridItem>
            <GridItem colSpan={2}>
              <FormLabel className="setting-description">{translate('VESSEL_NAMES_DESC')}</FormLabel>
            </GridItem>
            <GridItem className="chart-setting-container">
              <Switch onChange={handleQuiverPlotToggle} isChecked={false} disabled />
            </GridItem>
            <GridItem>
              <FormLabel className="setting-label">{translate('QUIVER_PLOT')}</FormLabel>
            </GridItem>
            <GridItem colSpan={2}>
              <FormLabel className="setting-description">{translate('QUIVER_PLOT_DESC')}</FormLabel>
            </GridItem>
            <GridItem className="chart-setting-container">
              <Switch onChange={handleNauticalToggle} isChecked={showNauticalMap} />
            </GridItem>
            <GridItem>
              <FormLabel className="setting-label">{translate('NAUTICAL_PLOT')}</FormLabel>
            </GridItem>
            <GridItem colSpan={2}>
              <FormLabel className="setting-description">{translate('NAUTICAL_PLOT_DESC')}</FormLabel>
            </GridItem>
          </Grid>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default React.memo(ChartSettings);
